import { ausrehabApi as baseApi } from '../baseApi';
import type { QuestionProps, QuestionnaireProps } from '../../models';
import type { CreateQuestionPayload } from './apis.types';

const questionnaireMgmtApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		// List all questionnaire templates
		getQuestionnaires: builder.query<QuestionnaireProps[], void>({
			query: () => ({ url: 'questionnaires' }),
			providesTags: ['Questionnaire'],
		}),

		// Get a single questionnaire with sections + questions
		getQuestionnaire: builder.query<QuestionnaireProps, number>({
			query: (id) => ({ url: `questionnaires/${id}` }),
			providesTags: ['Questionnaire'],
		}),

		// Create a new questionnaire template (ADMIN)
		createQuestionnaire: builder.mutation<QuestionnaireProps, { name: string; description?: string | null }>({
			query: (payload) => ({ url: 'questionnaires', method: 'POST', body: payload }),
			invalidatesTags: ['Questionnaire'],
		}),

		// Update questionnaire name/description (ADMIN)
		updateQuestionnaire: builder.mutation<
			QuestionnaireProps,
			{ id: number; name?: string; description?: string | null }
		>({
			query: ({ id, ...payload }) => ({ url: `questionnaires/${id}`, method: 'PUT', body: payload }),
		}),

		// Delete a questionnaire (ADMIN)
		deleteQuestionnaire: builder.mutation<{ deleted: boolean }, number>({
			query: (id) => ({ url: `questionnaires/${id}`, method: 'DELETE' }),
			invalidatesTags: ['Questionnaire'],
		}),

		// Assign a section to a questionnaire (ADMIN)
		addSectionToQuestionnaire: builder.mutation<
			QuestionnaireProps,
			{ questionnaireId: number; sectionId: number; orderIndex?: number }
		>({
			query: ({ questionnaireId, ...body }) => ({
				url: `questionnaires/${questionnaireId}/sections`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Questionnaire'],
		}),

		// Remove a section from a questionnaire (ADMIN)
		removeSectionFromQuestionnaire: builder.mutation<
			QuestionnaireProps,
			{ questionnaireId: number; sectionId: number }
		>({
			query: ({ questionnaireId, sectionId }) => ({
				url: `questionnaires/${questionnaireId}/sections/${sectionId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Questionnaire'],
		}),

		// Reorder sections within a questionnaire (ADMIN)
		reorderQuestionnaireSections: builder.mutation<
			QuestionnaireProps,
			{ questionnaireId: number; sections: Array<{ sectionId: number; orderIndex: number }> }
		>({
			query: ({ questionnaireId, sections }) => ({
				url: `questionnaires/${questionnaireId}/sections/reorder`,
				method: 'PUT',
				body: { sections },
			}),
		}),

		createQuestion: builder.mutation<QuestionProps, CreateQuestionPayload>({
			query: (payload) => ({ url: 'questions', method: 'POST', body: payload }),
			invalidatesTags: ['Questionnaire'],
		}),

		updateQuestion: builder.mutation<QuestionProps, { id: number } & Partial<CreateQuestionPayload>>({
			query: ({ id, ...payload }) => ({ url: `questions/${id}`, method: 'PUT', body: payload }),
			invalidatesTags: ['Questionnaire'],
		}),

		deleteQuestion: builder.mutation<{ deleted: boolean }, number>({
			query: (id) => ({ url: `questions/${id}`, method: 'DELETE' }),
			invalidatesTags: ['Questionnaire'],
		}),

		reorderQuestions: builder.mutation<void, { questions: Array<{ id: number; orderIndex: number }> }>({
			query: (payload) => ({ url: 'questions/reorder', method: 'PUT', body: payload }),
			invalidatesTags: ['Questionnaire'],
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetQuestionnairesQuery,
	useGetQuestionnaireQuery,
	useCreateQuestionnaireMutation,
	useUpdateQuestionnaireMutation,
	useDeleteQuestionnaireMutation,
	useAddSectionToQuestionnaireMutation,
	useRemoveSectionFromQuestionnaireMutation,
	useReorderQuestionnaireSectionsMutation,
	useCreateQuestionMutation,
	useUpdateQuestionMutation,
	useDeleteQuestionMutation,
	useReorderQuestionsMutation,
} = questionnaireMgmtApi;
